
import {
  DEFAULT_WHEEL_PRIZES,
  WHEEL_COLORS,
  WHEEL_GAP_COLOR,
  WHEEL_GAP_PX,
  buildSymmetricWheelColors,
  getPrizeImage,
  roundWheelDeg,
} from "./lotteryUtils";

export default {
  name: "LotteryHeroWheel",
  props: {
    activity: {
      type: Object,
      default: () => ({}),
    },
    copy: {
      type: Object,
      required: true,
    },
    isEnded: {
      type: Boolean,
      default: false,
    },
    isDrawing: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    displayUserPoints: {
      type: String,
      default: "0",
    },
    drawnTodayText: {
      type: String,
      default: "0",
    },
    costText: {
      type: String,
      default: "",
    },
    dailyLimitText: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      rotation: 0,
      wheelSegmentColors: [],
      wheelColorSegmentCount: 0,
      wheelRadiusPx: 280,
    };
  },
  computed: {
    isPrizeExhaustedToday() {
      return this.activity.prizeExhaustedToday === true;
    },
    isDrawDisabled() {
      return this.isDrawing || this.loading || this.isPrizeExhaustedToday;
    },
    drawButtonText() {
      return this.isDrawing ? this.copy.drawing : this.copy.drawNow;
    },
    drawButtonStyle() {
      const text = String(this.drawButtonText || "");
      const visualLength = Array.from(text).reduce((total, char) => {
        return total + (/[\u0000-\u00ff]/.test(char) ? 0.6 : 1);
      }, 0);
      const scale = visualLength > 18 ? 0.68 : visualLength > 8 ? 0.78 : 1;

      return {
        "--draw-btn-font-scale": scale,
      };
    },
    wheelPrizes() {
      const list = Array.isArray(this.activity.prizeList) ? this.activity.prizeList.filter(Boolean) : [];
      return list.length ? list : DEFAULT_WHEEL_PRIZES;
    },
    wheelSegmentDegrees() {
      const count = this.wheelPrizes.length || 1;
      return 360 / count;
    },
    wheelPointerAngle() {
      return 0;
    },
    heroStyle() {
      const image = this.isMobile
        ? (this.activity.mobileBgImageUrl || this.activity.bgImageUrl)
        : this.activity.bgImageUrl;
      if (!image) {
        return {};
      }
      return {
        backgroundImage: `url(${image})`,
      };
    },
    wheelStyle() {
      return {
        backgroundColor: WHEEL_GAP_COLOR,
        backgroundImage: `conic-gradient(from 0deg at 50% 50%, ${this.wheelGradientStops})`,
        transform: `translateZ(0) rotate(${this.rotation}deg)`,
      };
    },
    wheelGradientStops() {
      const count = this.wheelPrizes.length || 1;
      const segmentDeg = roundWheelDeg(360 / count);

      return Array.from({ length: count }, (_, index) => {
        const start = roundWheelDeg(index * segmentDeg);
        const end = index === count - 1 ? 360 : roundWheelDeg((index + 1) * segmentDeg);
        const color = this.wheelSegmentColors[index] || WHEEL_COLORS[index % WHEEL_COLORS.length];

        return `${color} ${start}deg ${end}deg`;
      }).join(", ");
    },
  },
  watch: {
    wheelPrizes: {
      immediate: true,
      handler(prizes) {
        this.syncWheelColors(prizes);
      },
    },
    isMobile() {
      this.$nextTick(() => {
        this.measureWheelRadius();
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.measureWheelRadius();
    });
  },
  methods: {
    // 通知页面执行抽奖接口，组件只负责触发交互。
    emitDraw() {
      this.$emit("draw");
    },
    // 通知页面滚动到活动规则模块。
    emitRules() {
      this.$emit("rules");
    },
    // 奖品变化时重新生成扇区配色，并在 DOM 更新后刷新半径。
    syncWheelColors(prizes) {
      const count = Array.isArray(prizes) ? prizes.length : 0;
      const nextCount = count || 1;
      const nextColors = buildSymmetricWheelColors(nextCount);
      const colorsInvalid = nextCount === 2
        && this.wheelSegmentColors.length === 2
        && this.wheelSegmentColors[0] === this.wheelSegmentColors[1];

      if (this.wheelColorSegmentCount !== nextCount || colorsInvalid) {
        this.wheelSegmentColors = nextColors;
        this.wheelColorSegmentCount = nextCount;
      }

      this.$nextTick(() => {
        this.measureWheelRadius();
      });
    },
    // 读取实际转盘尺寸，让奖品图文在不同屏幕下落在扇区中心。
    measureWheelRadius() {
      const wheel = this.$el && this.$el.querySelector(".lottery-wheel");
      if (!wheel) {
        return;
      }

      this.wheelRadiusPx = wheel.offsetWidth / 2;
    },
    // 根据扇区角度计算奖品图文模块的安全摆放尺寸。
    getPrizeModuleMetrics() {
      const outerRadius = this.wheelRadiusPx || 140;
      const innerRadius = outerRadius * (this.isMobile ? 0.28 : 0.3);
      const rimInset = outerRadius * 0.05;
      const span = Math.max(outerRadius - innerRadius - rimInset, 0);
      const segmentDeg = this.wheelSegmentDegrees;
      const halfRad = (segmentDeg * Math.PI) / 360;
      const moduleHeight = Math.max(28, span * 0.54);
      const moduleRadius = innerRadius + (span - moduleHeight) * 0.52 + moduleHeight / 2;
      const safeRadius = Math.min(moduleRadius, outerRadius - rimInset - moduleHeight / 2);
      const chordWidth = 2 * safeRadius * Math.sin(halfRad);
      const moduleWidth = Math.max(32, Math.min(chordWidth * 0.68, outerRadius * 0.52));
      const imageMaxHeight = moduleHeight * 0.8;

      return {
        moduleRadius: safeRadius,
        moduleWidth,
        moduleHeight,
        imageMaxHeight,
      };
    },
    // 输出单个奖品模块的旋转与位移样式。
    getPrizeModuleStyle(index) {
      const { moduleRadius, moduleWidth, moduleHeight, imageMaxHeight } = this.getPrizeModuleMetrics();
      const angle = roundWheelDeg(this.getPrizeSegmentCenter(index));

      return {
        width: `${moduleWidth}px`,
        height: `${moduleHeight}px`,
        "--prize-image-max-height": `${imageMaxHeight}px`,
        transform: `rotate(${angle}deg) translate(0, -${moduleRadius}px) translate(-50%, -50%)`,
      };
    },
    // 计算奖品所在扇区的中心角度。
    getPrizeSegmentCenter(index) {
      const segmentDeg = this.wheelSegmentDegrees;
      return index * segmentDeg + segmentDeg / 2;
    },
    // 输出扇区分割线样式。
    getWheelDividerStyle(index) {
      const boundaryDeg = roundWheelDeg(index * this.wheelSegmentDegrees);
      const halfGap = WHEEL_GAP_PX / 2;

      return {
        width: `${WHEEL_GAP_PX}px`,
        marginLeft: `${-halfGap}px`,
        transform: `translate(-50%, 0) rotate(${boundaryDeg}deg)`,
      };
    },
    // 匹配抽奖结果在当前转盘奖品列表中的位置。
    findPrizeWheelIndex(prize) {
      if (!prize) {
        return 0;
      }

      const wheelPrizeId = prize.prizeId ?? prize.prize_id ?? prize.id;
      if (wheelPrizeId !== undefined && wheelPrizeId !== null && wheelPrizeId !== "") {
        const indexById = this.wheelPrizes.findIndex((item) => {
          return (
            String(item.id) === String(wheelPrizeId) ||
            String(item.prizeId) === String(wheelPrizeId)
          );
        });

        if (indexById >= 0) {
          return indexById;
        }
      }

      const prizeName = prize.name ?? prize.prizeName;
      if (prizeName) {
        const indexByName = this.wheelPrizes.findIndex((item) => item.name === prizeName);

        if (indexByName >= 0) {
          return indexByName;
        }
      }

      return 0;
    },
    // 对外暴露给页面调用，按中奖奖品旋转到指针位置。
    spinToPrize(prize) {
      const index = this.findPrizeWheelIndex(prize);
      const segmentCenter = this.getPrizeSegmentCenter(index);
      const pointerAngle = this.wheelPointerAngle;
      const currentRotation = ((this.rotation % 360) + 360) % 360;
      const targetRotation = (pointerAngle - segmentCenter + 360) % 360;
      let delta = (targetRotation - currentRotation + 360) % 360;

      if (delta < 1) {
        delta += 360;
      }

      this.rotation += delta + 360 * 5;
    },
    // 转盘奖品名称只展示短文案，避免挤出扇区。
    getPrizeWheelName(prize) {
      const name = (prize?.name || prize?.prizeName || "").trim();
      if (name.length <= 16) {
        return name;
      }
      return `${name.slice(0, 16)}...`;
    },
    // 统一从奖品对象中取图并追加缩略参数。
    getPrizeImage,
  },
};
